import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";

import { useForgotPasswordMutation } from "../api/useForgotPasswordMutation";

import { Alert, AlertTitle } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { ROUTES } from "@/constants/routes";
import { ErrorResponse } from "@/types/response/ErrorResponse";
import { ValidationResponse } from "@/types/response/ValidationResponse";

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm() {
  const { t } = useTranslation();
  const forgotPasswordMutation = useForgotPasswordMutation();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate({
      email: data.email,
    });
  };

  // Get field errors from mutation error
  const getFieldError = (field: string) => {
    const response = forgotPasswordMutation.failureReason?.response;
    if (!response) {
      return undefined;
    }
    const error = response.data;
    if (!error || !Array.isArray(error)) {
      return undefined;
    }
    const validationError = (error as ValidationResponse).find(
      (e) => e.propertyName === field,
    );
    if (!validationError) return undefined;
    return t(`errorCodes.${validationError.errorMessage}`);
  };

  // Get auth error (non-validation errors)
  const getAuthError = () => {
    const response = forgotPasswordMutation.failureReason?.response;
    if (!response) {
      return undefined;
    }
    const error = response.data;
    if (!error || !("code" in error)) {
      return undefined;
    }
    return t(`errorCodes.${(error as ErrorResponse).code}`);
  };

  const authError = getAuthError();
  const emailError = getFieldError("email");

  // Sync server errors to form
  useEffect(() => {
    if (emailError) {
      form.setError("email", { message: emailError });
    }
  }, [emailError, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {authError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{authError}</AlertTitle>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("common.email")}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t("common.email")}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    forgotPasswordMutation.reset();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            disabled={forgotPasswordMutation.isPending}
            className="w-full"
          >
            {forgotPasswordMutation.isPending
              ? t("common.loading", "Loading...")
              : t("auth.pages.forgotPassword.submitButton")}
          </Button>
          <Button variant="ghost" className="w-full" asChild>
            <Link to={ROUTES.login}>
              {t("auth.pages.forgotPassword.backToSignIn")}
            </Link>
          </Button>
        </div>
      </form>
    </Form>
  );
}
